import { useEffect, useRef, useState, useCallback } from 'react'
import { ref, getDownloadURL } from 'firebase/storage'
import { storage } from '../lib/firebase.js'
import { useYouTubePlayer } from './useYouTubePlayer.js'
import type { PlayerContextType } from './usePlayer.js'

const DRIFT_THRESHOLD_S = 0.3

function extractYouTubeId(url: string | undefined | null): string | null {
  if (!url) return null
  try {
    const u = new URL(url)
    if (u.hostname.includes('youtube.com')) return u.searchParams.get('v')
    if (u.hostname === 'youtu.be') return u.pathname.slice(1) || null
  } catch { /* not a valid URL */ }
  return null
}

interface UseVideoSyncOptions {
  player: PlayerContextType
  containerId: string
  enabled: boolean
}

interface UseVideoSyncResult {
  youtubeId: string | null
  videoSrc: string | null
  videoRef: React.RefObject<HTMLVideoElement>
  hasVideo: boolean
  isYouTube: boolean
  ytReady: boolean
}

export function useVideoSync({ player, containerId, enabled }: UseVideoSyncOptions): UseVideoSyncResult {
  const youtubeId = extractYouTubeId(player.jobMeta?.videoUrl)
  const isYouTube = !!youtubeId

  // Native video: resolve originalStoragePath → download URL
  const [videoSrc, setVideoSrc] = useState<string | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null!)
  const originalPath = player.jobMeta?.originalStoragePath

  useEffect(() => {
    if (isYouTube || !originalPath) { setVideoSrc(null); return }
    if (originalPath.startsWith('http')) { setVideoSrc(originalPath); return }
    let cancelled = false
    getDownloadURL(ref(storage, originalPath))
      .then(url => { if (!cancelled) setVideoSrc(url) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [isYouTube, originalPath])

  const hasVideo = isYouTube || !!videoSrc

  // YouTube player
  const { playerRef, ytReady } = useYouTubePlayer({
    containerId,
    videoId: youtubeId ?? '',
    enabled: enabled && isYouTube,
  })

  // Sync video play/pause/seek with audio (audio is master clock from usePlayer)
  const prevPlaying = useRef(player.playing)
  const prevTime = useRef(player.currentTime)

  // Play/pause sync
  useEffect(() => {
    if (!enabled || !hasVideo) return
    const wasPlaying = prevPlaying.current
    prevPlaying.current = player.playing

    if (player.playing && !wasPlaying) {
      // Audio started — start video muted
      if (isYouTube && ytReady && playerRef.current) {
        playerRef.current.mute()
        playerRef.current.seekTo(player.currentTime, true)
        playerRef.current.playVideo()
      } else if (videoRef.current) {
        videoRef.current.muted = true
        videoRef.current.currentTime = player.currentTime
        void videoRef.current.play()
      }
    } else if (!player.playing && wasPlaying) {
      // Audio paused — pause video
      if (isYouTube && playerRef.current) {
        playerRef.current.pauseVideo()
      } else if (videoRef.current) {
        videoRef.current.pause()
      }
    }
  }, [player.playing, enabled, hasVideo, isYouTube, ytReady, playerRef, player.currentTime])

  // Seek sync — detect large jumps in currentTime
  useEffect(() => {
    if (!enabled || !hasVideo || !player.playing) return
    const delta = Math.abs(player.currentTime - prevTime.current)
    prevTime.current = player.currentTime

    // Only sync on seeks (>1s jump), not regular timeupdate ticks
    if (delta > 1) {
      if (isYouTube && playerRef.current) {
        playerRef.current.seekTo(player.currentTime, true)
      } else if (videoRef.current) {
        videoRef.current.currentTime = player.currentTime
      }
    }
  }, [player.currentTime, enabled, hasVideo, isYouTube, playerRef])

  // Drift correction every 1s
  useEffect(() => {
    if (!enabled || !hasVideo || !player.playing) return

    const driftId = setInterval(() => {
      const audioTime = player.currentTime
      if (isYouTube && playerRef.current) {
        const ytTime = playerRef.current.getCurrentTime()
        if (Math.abs(ytTime - audioTime) > DRIFT_THRESHOLD_S) {
          playerRef.current.seekTo(audioTime, true)
        }
      } else if (videoRef.current) {
        if (Math.abs(videoRef.current.currentTime - audioTime) > DRIFT_THRESHOLD_S) {
          videoRef.current.currentTime = audioTime
        }
      }
    }, 1000)

    return () => clearInterval(driftId)
  }, [enabled, hasVideo, isYouTube, player.playing, player.currentTime, playerRef])

  // Ensure video starts synced when YouTube becomes ready while audio is already playing
  const handleYtReady = useCallback(() => {
    if (player.playing && playerRef.current) {
      playerRef.current.mute()
      playerRef.current.seekTo(player.currentTime, true)
      playerRef.current.playVideo()
    }
  }, [player.playing, player.currentTime, playerRef])

  useEffect(() => {
    if (ytReady && enabled) handleYtReady()
  }, [ytReady, enabled, handleYtReady])

  return { youtubeId, videoSrc, videoRef, hasVideo, isYouTube, ytReady }
}
